<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useReportStore } from '@/store/report'
import {
  countCorrelatedCharts,
  type CorrelatedChartPair,
  type CorrelatedChartType,
  type ICorrelatedChartsPresent,
} from '@/utils/report/correlated-charts-layout'
import ReadingsDemandChart from './ReadingsDemandChart.vue'
import TemperatureChart from './TemperatureChart.vue'
import DoorOpenTimeline from './DoorOpenTimeline.vue'

const {
  correlatedChartsPresentBase,
  correlatedChartsPresentCompare,
} = storeToRefs(useReportStore())

const presentBase = computed(() => correlatedChartsPresentBase.value)
const presentCompare = computed(() => correlatedChartsPresentCompare.value)

const hasBlock = computed(
  () =>
    countCorrelatedCharts(presentBase.value) >= 1 ||
    countCorrelatedCharts(presentCompare.value) >= 1,
)

/** Ordem histórica: por tipo, base e comparação logo abaixo. */
const CHART_TYPE_ORDER: CorrelatedChartType[] = ['demand', 'temperature', 'door']

function shouldShowXAxisLabels(
  pair: CorrelatedChartPair,
  chartType: CorrelatedChartType,
  base: ICorrelatedChartsPresent,
  compare: ICorrelatedChartsPresent,
): boolean {
  if (pair === 'compare') return compare[chartType]
  // base: só mostra labels se não houver o mesmo tipo na comparação (ele fica abaixo)
  return base[chartType] && !compare[chartType]
}
</script>

<template>
  <div v-if="hasBlock" class="flex min-w-0 flex-col gap-6">
    <template v-for="chartType in CHART_TYPE_ORDER" :key="chartType">
      <ReadingsDemandChart
        v-if="chartType === 'demand' && presentBase.demand"
        pair="base"
        :show-x-axis-labels="shouldShowXAxisLabels('base', 'demand', presentBase, presentCompare)" />
      <ReadingsDemandChart
        v-if="chartType === 'demand' && presentCompare.demand"
        pair="compare"
        :show-x-axis-labels="shouldShowXAxisLabels('compare', 'demand', presentBase, presentCompare)" />

      <TemperatureChart
        v-if="chartType === 'temperature' && presentBase.temperature"
        pair="base"
        :show-x-axis-labels="shouldShowXAxisLabels('base', 'temperature', presentBase, presentCompare)" />
      <TemperatureChart
        v-if="chartType === 'temperature' && presentCompare.temperature"
        pair="compare"
        :show-x-axis-labels="shouldShowXAxisLabels('compare', 'temperature', presentBase, presentCompare)" />

      <DoorOpenTimeline
        v-if="chartType === 'door' && presentBase.door"
        pair="base"
        :show-x-axis-labels="shouldShowXAxisLabels('base', 'door', presentBase, presentCompare)" />
      <DoorOpenTimeline
        v-if="chartType === 'door' && presentCompare.door"
        pair="compare"
        :show-x-axis-labels="shouldShowXAxisLabels('compare', 'door', presentBase, presentCompare)" />
    </template>
  </div>
</template>
